<template>

  <div
    class   = "d-flex align-center item-container"
    :class  = "{ alternate: (index % 2) === 1 }"
    :style  = "clipStyle"
  >

    <!-- Index -->
    <div class="d-flex flex-column align-center mx-2">

      <v-btn
        icon
        plain
        small
        :disabled   = "!canMoveUp"
        @click      = "onMoveUp"
      >
        <v-icon small>mdi-chevron-up</v-icon>
      </v-btn>

      <span class="text-subtitle-2 text--secondary">
        {{ index + 1 }}
      </span>

      <v-btn
        icon
        plain
        small
        :disabled   = "!canMoveDown"
        @click      = "onMoveDown"
      >
        <v-icon small>mdi-chevron-down</v-icon>
      </v-btn>

    </div>

    <!-- Thumbnail -->
    <div class = "thumbnail mr-4">
      <img 
        v-if    = "clip.thumbnail" 
        :src    = "clip.thumbnail"
      >
      <div 
        v-else
        class   = "no-thumbnail"
      >
        <v-icon color="rgba(255, 255, 255, 0.2)">mdi-image</v-icon>
      </div>

      <div
        class   = "trigger-overlay"
        :class  = "{ interactive: canEdit }"
        @click  = "onUpdateThumbnail"
      >
        <v-icon>mdi-camera</v-icon>
      </div>

    </div>

    <!-- Clip Info -->
    <div class="d-flex flex-column mb-1">

      <div class="filename d-flex text-subtitle-2">
        <v-spacer></v-spacer>

        <v-tooltip
          bottom
        >

          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-on    = "on"
              v-bind  = "attrs"
              icon
              small
            >
              <v-icon small>mdi-information-outline</v-icon>
            </v-btn>
          </template>

          <span>{{ clip.path }}</span>

        </v-tooltip>
        
        <a 
          class   = "short-path text-truncate" 
          @click  = "onOpen"
        >
          {{ path.filename }}
        </a>
      </div>

      <!-- Markers -->
      <div class="markers">

        <span class="text-caption text--secondary mr-2 label">In</span>

        <timecode-input
          class     = "timecode"
          small
          :disabled = "!canEdit"
          :error    = "doesClipHaveRangeError"
          :value    = "inTimecode"
          @input    = "onChangeInTimecode"
        >
        </timecode-input>

        <v-btn
          class     = "ml-2"
          icon
          small
          :disabled = "!canEdit"
          @click    = "onJumpToInTimecode"
        >
          <v-icon small>mdi-page-first</v-icon>
        </v-btn>

        <v-btn
          icon
          small
          :disabled = "!canEdit"
          @click    = "onUpdateInTimecode"
        >
          <v-icon small>mdi-clock-outline</v-icon>
        </v-btn>

        <span class="text-caption text--secondary mr-2 label">Out</span>

        <timecode-input
          class     = "timecode"
          small
          :disabled = "!canEdit"
          :error    = "doesClipHaveRangeError"
          :value    = "outTimecode"
          @input    = "onChangeOutTimecode"
        >
        </timecode-input>

        <v-btn
          class     = "ml-2"
          icon
          small
          :disabled = "!canEdit"
          @click    = "onJumpToOutTimecode"
        >
          <v-icon small>mdi-page-last</v-icon>
        </v-btn>

        <v-btn
          icon
          small
          :disabled = "!canEdit"
          @click    = "onUpdateOutTimecode"
        >
          <v-icon small>mdi-clock-outline</v-icon>
        </v-btn>

      </div>

    </div>

    <!-- Notes -->
    <v-textarea
      class     = "flex-grow-1 mx-2"
      outlined
      dense
      no-resize
      label     = "Notes"
      rows      = "2"
      hide-details
      :disabled = "!canEdit"
      :value    = "clip.notes"
      @change   = "onNotesChanged"
    >
    </v-textarea>

    <!-- Remove -->
    <v-btn
      class     = "ml-1 mr-2"
      icon
      @click    = "onRemove"
    >
      <v-icon>mdi-delete</v-icon>
    </v-btn>

    <ConfirmationDialog ref="confirmation_dialog"></ConfirmationDialog>

  </div>

</template>

<script>

// #region Imports

/* Vuex */
import { mapState } from 'vuex';

/* Telestream */
import {
  ToTimeCode,
  ToSeconds,
  ToFrames,
  AddTimecodes,
  SubtractTimecodes
} from '@user-interface/timecode-utilities';
import {
  JUMP_TO_TIME,
  LOAD_VIDEO,
  MOVE_CLIP_DOWN,
  MOVE_CLIP_UP,
  REMOVE_CLIP,
  UPDATE_CLIP,
  UPDATE_CLIP_THUMBNAIL
} from '@/utils/action-types';
import { splitPath } from '@/components/file-toolbar/utilities/path.utils';
import ConfirmationDialog from '../dialogs/ConfirmationDialog';

// #endregion Imports

export default {

  // #region Properties

  name:  'ClipItem',
  props: {
    index: Number,
    clip:  Object
  },
  computed: {
    ...mapState([
      'videoPath',
      'video',
      'clips'
    ]),
    canMoveUp() {
      return this.index > 0;
    },
    canMoveDown() {
      return this.index < (this.clips.clips.length - 1);
    },
    canEdit() {
      return this.clip.path === this.videoPath;
    },
    doesClipHaveRangeError() {
      return this.clip.in > this.clip.out;
    },
    clipStyle() {
      return {
        'border-left': `4px solid ${this.clip.color}`
      }
    },
    path() {
      return splitPath(this.clip.path);
    },
    inTimecode() {
      const timecode = ToTimeCode(this.clip.in, this.clip.framerate, this.clip.dropframe);
      return AddTimecodes(timecode, this.clip.offset);
    },
    outTimecode() {
      const timecode = ToTimeCode(this.clip.out, this.clip.framerate, this.clip.dropframe);
      return AddTimecodes(timecode, this.clip.offset);
    }
  },
  components: {
    ConfirmationDialog
  },

  // #endregion Properties

  // #region Methods

  methods: {

    // #region Events

    onMoveUp() {
      this.$store.dispatch(MOVE_CLIP_UP, this.clip.id);
    },
    onMoveDown() {
      this.$store.dispatch(MOVE_CLIP_DOWN, this.clip.id);
    },
    onUpdateThumbnail() {
      this.$store.dispatch(UPDATE_CLIP_THUMBNAIL, this.clip.id);
    },
    async onOpen() {
      await this.$store.dispatch(
        LOAD_VIDEO,
        {
          videoPath: this.clip.path
        }
      );

      this.onJumpToInTimecode();
    },
    onChangeInTimecode(timecode) {
      const frames = ToFrames(SubtractTimecodes(timecode, this.clip.offset));
      const updatedClip = {
        ...this.clip,
        in: frames
      };

      this.$store.dispatch(UPDATE_CLIP, updatedClip);
    },
    onJumpToInTimecode() {
      const timecode = ToTimeCode(this.clip.in, this.clip.framerate, this.clip.dropframe);
      const time = ToSeconds(timecode, true);

      this.$store.dispatch(JUMP_TO_TIME, time);
    },
    onUpdateInTimecode() {
      const updatedClip = {
        ...this.clip,
        in:        this.video.currentFrame,
        framerate: this.video.framerate,
        dropframe: this.video.firstTimecode.includes(';'),
        offset:    this.video.firstTimecode
      };

      this.$store.dispatch(UPDATE_CLIP, updatedClip);
    },
    onChangeOutTimecode(timecode) {
      const frames = ToFrames(SubtractTimecodes(timecode, this.clip.offset));
      const updatedClip = {
        ...this.clip,
        out: frames
      };

      this.$store.dispatch(UPDATE_CLIP, updatedClip);
    },
    onJumpToOutTimecode() {
      const timecode = ToTimeCode(this.clip.out, this.clip.framerate, this.clip.dropframe);
      const time = ToSeconds(timecode, true);

      this.$store.dispatch(JUMP_TO_TIME, time);
    },
    onUpdateOutTimecode() {
      const updatedClip = {
        ...this.clip,
        out: this.video.currentFrame
      };

      this.$store.dispatch(UPDATE_CLIP, updatedClip);
    },
    onNotesChanged(notes) {
      const updatedClip = {
        ...this.clip,
        notes: notes
      };

      this.$store.dispatch(UPDATE_CLIP, updatedClip);
    },
    async onRemove() {
      if (await this.$refs.confirmation_dialog.confirm(
        'Remove Clip?',
        `Are you sure you want remove clip ${this.index + 1}? This operation cannot be undone.`,
        'Remove Clip',
        'error',
        'Keep Clip',
        'primary'
      )) {
        this.$store.dispatch(REMOVE_CLIP, this.clip.id);
      }
    }

    // #endregion Events

  }

  // #endregion Methods

}

</script>

<style lang="scss" scoped>

.alternate {
  background-color: rgba(255, 255, 255, 0.04);
}

.thumbnail {
  height:     56px;

  position:   relative;

  .no-thumbnail {
    width:      100px;
    height:     56px;

    background: #151515;

    display:          flex;
    align-items:      center;
    justify-content:  center;    
  }

  .trigger-overlay {

    position:   absolute;
    top:        0px;
    right:      0px;
    bottom:     0px;
    left:       0px;

    display:          flex;
    align-items:      center;
    justify-content:  center;

    pointer-events: none;
    .v-icon {
      visibility: hidden;
    }

    &.interactive {
      cursor:         pointer;
      pointer-events: all;

      &:hover {
        background-color: rgba(255, 255, 255, 0.15);
        .v-icon {
          visibility: visible;
        }
      }
    }
  }
}

.markers {
  display:                grid;
  align-items:            center;
  grid-template-columns:  repeat(4, auto);

  .label {
    grid-column:    0;
    text-align:     right;
  }
}

.timecode {
  font-size:    0.9em;
}

.filename {
  position:     relative;

  a {
    color:      white;
    &:hover {
      text-decoration: underline;
    }
  }

  .short-path {
    position:     absolute;
    top:          4px;
    left:         0px;
    right:        25px;
    overflow:     hidden;
    white-space:  nowrap;

  }
}

</style>